import type {
  AuthoredNumber,
  BossBehaviorDataDocument,
  EnemyBehavior,
  EnemyStatsDocument,
  EntityBehaviorDataDocument,
  EntityTypeId,
  GeneratorId,
  ProjectilePatternDocument,
  RangedBehaviorDataDocument,
} from "../content/types";
import { Point, Rectangle } from "../runtime";
import {
  ENEMY_INTRO_COUNTER,
  SLASH_NONE,
  STATIC_ENTITY_STATS,
  type SlashTuning,
  rollValue,
} from "./data/entityStats";
import type { SheetClass } from "./data/entitySheets";
import { blockMovementAxes } from "./combat";
import type { Obstacle } from "./level";
import { EntityId } from "./ids";

/** Enemy fire control, or a projectile's payload. Each object uses one shape or the other. */
interface ShotData {
  /** Enemy: 1 while the player is inside 350px. */
  inRange?: number;
  /** Projectile: damage dealt on contact. */
  damage?: number;
}

export interface EnemyConstruction {
  readonly entityType: EntityTypeId;
  readonly behavior: EnemyBehavior;
  readonly stats: EnemyStatsDocument;
  readonly behaviorData?: EntityBehaviorDataDocument;
  readonly spriteClass: SheetClass;
  readonly largeEffects: boolean;
  readonly x: number;
  readonly y: number;
  readonly levelWidth: number;
  readonly levelHeight: number;
  /** Base values used before a boss variant applies, preserving spawn rolls. */
  readonly initialPreferredDistance?: AuthoredNumber;
  readonly initialFrameCount?: number;
}

/** The per-enemy bag `ObjectGenerator` and `Game` read while an enemy is alive. */
export interface RollData {
  rolling: boolean;
  rollCounter: number;
  rollLimit: number;
  /** Set once the death slash has already spawned its reward and drops. */
  secondary: boolean;
  hpBarCD: number;
  hpCurrent: number;
  vanished: boolean;
  /** 0 idle, 1-4 the slash sequence, 10-13 the mine's charge and chase. */
  slashState: number;
  counter: number;
  shotOffsetX: number;
  shotOffsetY: number;
  rewardPoints: number;
  dropChanceXenos: number;
  dropChanceHP: number;
  hpBarX: number;
  /** Half the enemies fire on each pass of a shot cycle. */
  alternate: boolean;
  blackHoleFrame: number;
  /** Authored generator target identity. */
  targetID?: GeneratorId;
}

/** Movement and animation tuning fixed at construction. */
export interface EnemyData {
  angleCounter: number;
  angleLimit: number;
  playerDist: number;
  xOffset: number;
  yOffset: number;
  speedNormal: number;
  speedRolling: number;
  armour: number;
  introCounter: number;
  animOffsetRoll: number;
  frameLimitNormal: number;
  frameLimitRoll: number;
  animSpeed: number;
  stopper: boolean;
  wallPass: boolean;
}

export interface SlashData extends SlashTuning {
  readonly slasher: boolean;
  counter: number;
}

export class Entity {
  tileRect!: Rectangle;
  readonly finalPos = new Point(0, 0);
  rollData!: RollData;
  shotData!: ShotData;
  readonly id: EntityId | null;
  readonly entityType: EntityTypeId | null;
  readonly spriteClass: SheetClass | null;
  readonly behavior: EnemyBehavior | null;
  readonly behaviorData: EntityBehaviorDataDocument | null;
  readonly rangedBehavior: RangedBehaviorDataDocument | null;
  readonly bossBehavior: BossBehaviorDataDocument | null;
  private readonly largeEffects: boolean;
  readonly warpsOnRoll: boolean;
  readonly projectileChannel: string | null;
  readonly projectilePattern: ProjectilePatternDocument | null;
  xPos = 0;
  yPos = 0;
  flipped = false;
  inBlackHole = false;
  targetAngle = 0;
  life = 0;
  enemy = false;
  hitRadius = 0;
  projectile = false;
  dead = false;
  protected enemyData!: EnemyData;
  protected slashData!: SlashData;
  private tileSize = 48;
  private rowLength = 0;
  private animLoop = true;
  protected frame = 0;
  protected animSpeed = 3;
  protected frameCounter = 0;
  protected frameLimit = 0;
  protected animOffset = 0;
  private readonly moveAllowedPrevious: [boolean, boolean] = [false, false];
  private readonly moveAllowedCurrent: [boolean, boolean] = [true, true];
  protected xTarget = 0;
  protected yTarget = 0;
  protected baseSpeed = NaN;
  protected xSpeed = 0;
  protected ySpeed = 0;
  protected moving = false;
  private readonly capturedPosition = new Point(0, 0);
  protected actionCounter = 0;
  protected actionLimit = 0;
  protected lifeLimit = 0;
  protected levelWidth = 0;
  protected levelHeight = 0;
  protected deathCounter = 0;
  protected hitDelay = 0;
  constructor(
    init: EntityId | EnemyConstruction = EntityId.Spark,
    x = 0,
    y = 0,
    extraValue = 0,
  ) {
    if (typeof init === "number") {
      if (init < EntityId.Spark) {
        throw new Error(
          "authored enemies must be constructed by EntityFactory",
        );
      }
      this.id = init;
      this.entityType = null;
      this.spriteClass = null;
      this.behaviorData = null;
      this.rangedBehavior = null;
      this.bossBehavior = null;
      this.largeEffects = false;
      this.behavior = null;
      this.warpsOnRoll = false;
      this.projectileChannel = null;
      this.projectilePattern = null;
      this.initializeEffect(init, x, y, extraValue);
      return;
    }
    this.id = null;
    this.entityType = init.entityType;
    this.spriteClass = init.spriteClass;
    const behaviorData = init.behaviorData;
    this.rangedBehavior =
      behaviorData !== undefined && "projectilePattern" in behaviorData
        ? behaviorData
        : null;
    this.bossBehavior =
      behaviorData !== undefined && "rig" in behaviorData ? behaviorData : null;
    this.behaviorData = behaviorData ?? null;
    this.largeEffects = init.largeEffects;
    this.behavior = init.behavior;
    this.projectileChannel = this.rangedBehavior?.channel ?? null;
    this.projectilePattern = this.rangedBehavior?.projectilePattern ?? null;
    this.warpsOnRoll = this.bossBehavior?.warpOnRoll ?? false;
    this.initializeEnemy(init);
  }

  get isMine(): boolean {
    return false;
  }

  get isGenerator(): boolean {
    return false;
  }

  get isBoss(): boolean {
    return false;
  }

  get isHeavyMelee(): boolean {
    return false;
  }

  get usesLargeEffects(): boolean {
    return this.largeEffects;
  }

  get keepsFacingSheet(): boolean {
    return false;
  }

  get dropsItems(): boolean {
    return !this.isMine && !this.isGenerator && !this.isBoss;
  }

  get meleeDamage(): number {
    return 0;
  }
  get rewardPoints(): number {
    return this.rollData.rewardPoints;
  }

  get canBeDamagedByCleanser(): boolean {
    return !this.isGenerator;
  }

  bossEvent(mode = 0): void {
    this.updateBossEvent(mode);
  }

  private initializeEnemy(init: EnemyConstruction): void {
    const stats = init.stats;
    const slash = stats.slash;
    this.enemy = true;
    this.slashData = {
      slasher: slash != null,
      counter: 0,
      ...(slash == null
        ? SLASH_NONE
        : {
            animOffset: slash.animationOffset,
            frameLimit: slash.frameCount,
            freezeFrame: slash.freezeFrame,
            slashRange: slash.range,
            freq: slash.frequency,
          }),
    };
    this.shotData = { inRange: 0 };
    // preserve the original sequence: intro, preferred distance, orbit, frame
    this.enemyData = {
      angleCounter: 150,
      angleLimit: 150,
      xOffset: 0,
      yOffset: 0,
      introCounter:
        stats.initialActionDelay === undefined
          ? rollValue(ENEMY_INTRO_COUNTER)
          : rollAuthored(stats.initialActionDelay),
      playerDist: rollAuthored(
        init.initialPreferredDistance ?? stats.preferredDistance,
      ),
      speedNormal: stats.speed,
      speedRolling: stats.rollSpeed ?? 0,
      armour: stats.armour ?? 0,
      animOffsetRoll: stats.rollAnimationOffset,
      frameLimitNormal: stats.idleFrameCount,
      frameLimitRoll: stats.rollFrameCount,
      animSpeed: stats.animationSpeed ?? 2,
      stopper: stats.pausesBetweenMoves ?? true,
      wallPass: stats.passesWalls ?? false,
    };
    const projectileOffset = stats.projectileOffset;
    this.rollData = {
      rolling: false,
      rollCounter: 0,
      rollLimit: stats.rollLimit ?? 30,
      secondary: false,
      hpBarCD: 0,
      hpCurrent: 100,
      vanished: true,
      slashState: 0,
      counter: 0,
      shotOffsetX: projectileOffset?.x ?? 0,
      shotOffsetY: projectileOffset?.y ?? 0,
      rewardPoints: stats.rewardPoints ?? 0,
      dropChanceXenos: stats.xenosDropChance ?? 0,
      dropChanceHP: stats.healthDropChance ?? 0,
      hpBarX: 0,
      alternate: false,
      blackHoleFrame: 0,
    };
    this.levelWidth = init.levelWidth;
    this.levelHeight = init.levelHeight;
    this.xPos = init.x;
    this.yPos = init.y;
    this.hitRadius = stats.hitRadius;
    this.tileSize = stats.tileSize;
    this.moving = stats.startsMoving ?? false;
    this.rollData.hpBarX = this.tileSize * 0.5;
    this.animSpeed = this.enemyData.animSpeed;
    this.baseSpeed = this.enemyData.speedNormal;
    this.frameLimit = this.enemyData.frameLimitNormal;
    this.setNewAngle();
    this.setActionLimit(0);
    this.actionCounter += this.enemyData.introCounter;
    if (
      init.initialPreferredDistance !== undefined &&
      typeof stats.preferredDistance === "number"
    ) {
      // boss variants keep the base orbit selected before variant stats apply
      this.enemyData.playerDist = stats.preferredDistance;
    }
    const initialFrameCount = init.initialFrameCount ?? this.frameLimit;
    this.frame = Math.ceil(Math.random() * (initialFrameCount - 1)) - 1;
    this.finishInitialization(stats.bitmapWidth);
  }

  private initializeEffect(
    id: EntityId,
    x: number,
    y: number,
    extraValue: number,
  ): void {
    this.animLoop = false;
    let bitmapWidth = 0;
    let randomFrame = false;
    const stats = STATIC_ENTITY_STATS[id];
    if (stats) {
      this.tileSize = stats.tileSize;
      bitmapWidth = stats.bitmapWidth;
      this.frameLimit = stats.frameLimit;
      this.animSpeed = stats.animSpeed;
      this.animLoop = stats.animLoop ?? false;
      this.animOffset = stats.animOffset ?? 0;
      randomFrame = stats.randomFrame ?? false;
      if (stats.frame) this.frame = rollValue(stats.frame);
      const motion = stats.motion;
      if (motion) {
        this.moving = true;
        this.targetAngle = extraValue;
        this.baseSpeed = rollValue(motion.speed);
        if (motion.angleJitter) {
          this.targetAngle += rollValue(motion.angleJitter);
        }
        this.setAngle();
        if (motion.fork) {
          const side = Math.ceil(Math.random() * 2);
          if (side === 1) this.targetAngle += 45;
          if (side === 2) {
            this.targetAngle -= 45;
            this.actionLimit = 1;
          }
        }
      }
      const shot = stats.projectile;
      if (shot) {
        this.projectile = true;
        this.shotData = { damage: shot.damage };
        this.hitRadius = shot.hitRadius ?? 0;
        this.lifeLimit = rollValue(shot.lifeLimit);
      }
    }
    this.xPos = x;
    this.yPos = y;
    if (randomFrame) {
      this.frame = Math.ceil(Math.random() * (this.frameLimit - 1)) - 1;
    }
    this.finishInitialization(bitmapWidth);
  }

  private finishInitialization(bitmapWidth: number): void {
    this.xTarget = this.xPos;
    this.yTarget = this.yPos;
    this.updateFinalPos();
    this.rowLength = (bitmapWidth / this.tileSize) | 0;
    this.tileRect = new Rectangle(0, 0, this.tileSize, this.tileSize);
  }

  shove(shoveAngle: number): void {
    const rad = shoveAngle * (Math.PI / 180);
    this.xTarget += this.baseSpeed * 10 * Math.cos(rad);
    this.yTarget += this.baseSpeed * 10 * Math.sin(rad);
  }

  updateAnim(): number {
    let destroyed = 0;
    ++this.frameCounter;
    destroyed = this.updateBehaviorAnimation(destroyed);
    if (this.id === EntityId.Spark && this.frame === 2) {
      destroyed = 3;
    }
    if (this.frameCounter >= this.animSpeed) {
      this.frameCounter = 0;
      ++this.frame;
      if (this.frame === this.frameLimit) {
        if (this.animLoop === false) {
          destroyed = 2;
        } else if (
          this.enemy &&
          this.rollData.slashState !== 0 &&
          this.rollData.slashState < 10
        ) {
          // mid-slash the animation holds on its last frame instead of looping
          --this.frame;
        } else {
          this.frame = 0;
        }
      }
      this.drawTile(this.frame + this.animOffset);
    }
    if (
      this.enemy &&
      this.inBlackHole &&
      this.rollData.hpCurrent === 0 &&
      !this.isBoss
    ) {
      destroyed = 2;
    }
    if (this.enemy) {
      if (this.dead) {
        destroyed = this.updateDeath(destroyed);
      }
    } else if (this.dead) {
      destroyed = 2;
    }
    if (this.lifeLimit > 0) {
      ++this.life;
      if (this.life >= this.lifeLimit) {
        destroyed = 2;
      }
    }
    return destroyed;
  }

  enemyUpdate(
    knightX: number,
    knightY: number,
    obsArray: Obstacle[][],
    barrierArray: Obstacle[],
  ): number {
    let returnAction = 0;
    if (this.enemyData.introCounter < 26) {
      ++this.enemyData.introCounter;
      if (this.enemyData.introCounter === 1) {
        returnAction = 1;
      }
      if (this.enemyData.introCounter === 26) {
        this.rollData.vanished = false;
      }
    }
    if (this.dead === false) {
      if (this.inBlackHole === false) {
        if (this.rollData.slashState === 0) {
          if (this.moving) {
            returnAction = this.chase(knightX, knightY, returnAction);
          } else {
            this.slowDown(0.05);
          }
        } else {
          returnAction = this.updateSlash(knightX, knightY, returnAction);
        }
        this.xTarget += this.xSpeed;
        this.yTarget += this.ySpeed;
        let futureX =
          Math.round(this.xPos + (this.xTarget - this.xPos) * 0.075) | 0;
        let futureY =
          Math.round(this.yPos + (this.yTarget - this.yPos) * 0.075) | 0;
        if (this.xSpeed !== 0 && this.ySpeed !== 0) {
          let moveAllowed = this.collision(
            futureX,
            futureY,
            obsArray,
            barrierArray,
          );
          if (this.enemyData.wallPass === false) {
            if (this.moveAllowedPrevious[0] && this.moveAllowedPrevious[1]) {
              while (moveAllowed[0] === false && moveAllowed[1] === false) {
                if (this.xTarget > this.xPos) {
                  futureX--;
                }
                if (this.xTarget < this.xPos) {
                  futureX++;
                }
                if (this.yTarget > this.yPos) {
                  futureY--;
                }
                if (this.yTarget < this.yPos) {
                  futureY++;
                }
                moveAllowed = this.collision(
                  futureX,
                  futureY,
                  obsArray,
                  barrierArray,
                );
              }
            }
            this.moveAllowedPrevious[0] = moveAllowed[0];
            this.moveAllowedPrevious[1] = moveAllowed[1];
          }
          if (moveAllowed[0] || this.enemyData.wallPass) {
            this.xPos = futureX;
          } else {
            this.xTarget = this.xPos;
            if (this.enemyData.angleCounter > 5) {
              this.enemyData.angleCounter = 5;
            }
          }
          if (moveAllowed[1] || this.enemyData.wallPass) {
            this.yPos = futureY;
          } else {
            this.yTarget = this.yPos;
            if (this.enemyData.angleCounter > 5) {
              this.enemyData.angleCounter = 5;
            }
          }
          if (this.rollData.slashState === 0) {
            if (knightX > this.xPos) {
              if (this.flipped === false) {
                this.flipped = true;
                this.animOffset = this.keepsFacingSheet
                  ? 0
                  : this.enemyData.frameLimitNormal;
              }
            } else if (this.flipped) {
              this.flipped = false;
              this.animOffset = 0;
            }
          }
        }
        if (!this.isMine && this.actionCounter < this.actionLimit) {
          ++this.actionCounter;
        }
        if (this.enemyData.stopper) {
          if (this.actionCounter === this.actionLimit) {
            this.moving = !this.moving;
            if (this.moving) {
              this.xTarget = this.xPos;
              this.yTarget = this.yPos;
            }
            this.setActionLimit(1);
          }
        } else if (this.actionCounter === this.actionLimit) {
          this.moving = true;
          this.xTarget = this.xPos;
          this.yTarget = this.yPos;
          ++this.actionCounter;
        }
      } else {
        const bhOffsetX = Math.round(Math.random() * 50) * 0.1 - 0.25;
        const bhOffsetY = Math.round(Math.random() * 50) * 0.1 - 0.25;
        this.xPos = this.capturedPosition.x + bhOffsetX;
        this.yPos = this.capturedPosition.y + bhOffsetY;
      }
    } else if (this.rollData.rolling) {
      ++this.rollData.rollCounter;
      const futureXRoll = this.xPos + this.xSpeed;
      const futureYRoll = this.yPos + this.ySpeed;
      returnAction = this.updateRollingBehavior(returnAction);
      const moveAllowedRoll = this.collision(
        futureXRoll,
        futureYRoll,
        obsArray,
        barrierArray,
      );
      if (moveAllowedRoll[0] || this.enemyData.wallPass) {
        this.xPos = futureXRoll;
      } else {
        this.xSpeed *= -1;
        this.xPos += this.xSpeed;
      }
      if (moveAllowedRoll[1] || this.enemyData.wallPass) {
        this.yPos = futureYRoll;
      } else {
        this.ySpeed *= -1;
        this.yPos += this.ySpeed;
      }
    }
    this.updateFinalPos();
    return returnAction;
  }

  effectUpdate(): void {
    if (this.moving) {
      if (
        this.id === EntityId.RotatingLaser ||
        this.id === EntityId.LaserShot
      ) {
        if (this.id === EntityId.RotatingLaser) {
          this.targetAngle += 0.8;
        }
        const angleDivision = Math.floor(this.targetAngle / 360);
        let finalAngle = (this.targetAngle - 360 * angleDivision) | 0;
        if (finalAngle >= 180) {
          finalAngle -= 180;
        }
        this.animOffset = Math.floor(finalAngle * 0.1) * 2;
        this.setAngle();
      }
      if (this.id === EntityId.SickleShot) {
        if (this.actionLimit === 0) {
          this.targetAngle -= 2;
        }
        if (this.actionLimit === 1) {
          this.targetAngle += 2;
        }
        this.setAngle();
      }
      this.xPos += this.xSpeed;
      this.yPos += this.ySpeed;
    }
    this.updateFinalPos();
  }

  updateMissile(playerX: number, playerY: number): void {
    this.targetAngle =
      Math.atan2(playerY - this.yPos, playerX - this.xPos) * (180 / Math.PI);
    const radMissile = this.targetAngle * (Math.PI / 180);
    this.xSpeed += 0.22 * Math.cos(radMissile);
    this.ySpeed += 0.22 * Math.sin(radMissile);
    if (this.xSpeed > this.baseSpeed) {
      this.xSpeed = this.baseSpeed;
    }
    if (this.xSpeed < -this.baseSpeed) {
      this.xSpeed = -this.baseSpeed;
    }
    if (this.ySpeed > this.baseSpeed) {
      this.ySpeed = this.baseSpeed;
    }
    if (this.ySpeed < -this.baseSpeed) {
      this.ySpeed = -this.baseSpeed;
    }
    this.animOffset = Math.floor(this.targetAngle / 20) * 2;
    this.animOffset += 18;
    if (this.animOffset === 36) {
      this.animOffset = 0;
    }
  }

  private collision(
    futureX: number,
    futureY: number,
    obsArray: Obstacle[][],
    barriers: Obstacle[],
  ): [boolean, boolean] {
    const moveAllowed = blockMovementAxes(
      this.xPos,
      this.yPos,
      futureX,
      futureY,
      this.hitRadius,
      this.levelWidth,
      this.levelHeight,
      obsArray,
      barriers,
      this.moveAllowedCurrent,
    );
    this.onCollision(moveAllowed);
    return moveAllowed;
  }

  hit(hitD: number = 0, hitAngle: number = 0, damage: number = 0): number {
    if (this.dead === false || damage > 9000) {
      this.blackHole(false);
      if (this.rollData.vanished) {
        this.rollData.vanished = false;
      }
      this.hitDelay = hitD;
      this.targetAngle = hitAngle;
      this.dead = true;
      if (damage > 0) {
        this.rollData.hpCurrent -= Math.ceil(damage / this.enemyData.armour);
        if (this.rollData.hpCurrent < 0) {
          this.rollData.hpCurrent = 0;
        }
        this.rollData.hpBarCD = 100;
      }
      if (this.rollData.slashState > 0) {
        this.rollData.slashState = 0;
        this.slashData.counter = 0;
        this.actionCounter = 0;
        this.rollData.counter = 0;
      }
    }
    return 0;
  }

  blackHoleDamage(damage: number = 0): void {
    this.rollData.hpCurrent -= Math.ceil(damage / this.enemyData.armour);
    if (this.rollData.hpCurrent < 0) {
      this.rollData.hpCurrent = 0;
    }
    this.rollData.hpBarCD = 37;
  }

  holdActions(): void {
    if (this.slashData.slasher) {
      this.slashData.counter = 0;
    }
  }

  blackHole(enabled: boolean = true, bhFrame: number = 0): void {
    if (enabled) {
      if (this.inBlackHole === false) {
        this.inBlackHole = true;
        this.capturedPosition.x = this.xPos;
        this.capturedPosition.y = this.yPos;
        this.rollData.blackHoleFrame = bhFrame;
      }
    } else if (this.inBlackHole) {
      this.inBlackHole = false;
    }
  }

  protected updateBossEvent(_mode: number): void {
    throw new Error("bossEvent requires a boss entity");
  }

  teleport(x: number, y: number): void {
    this.xPos = x;
    this.yPos = y;
    this.xTarget = this.xPos;
    this.yTarget = this.yPos;
    this.xSpeed = 0;
    this.ySpeed = 0;
    this.updateFinalPos();
  }

  private updateFinalPos(): void {
    this.finalPos.x = this.xPos - this.tileSize * 0.5;
    this.finalPos.y = this.yPos - this.tileSize * 0.5;
  }

  /** Death roll bookkeeping, run from `updateAnim` while `dead` is set. */
  private updateDeath(destroyed: number): number {
    if (this.rollData.rolling === false) {
      ++this.deathCounter;
      if (this.hitDelay > 0 && this.deathCounter === this.hitDelay) {
        this.rollData.rolling = true;
        this.baseSpeed = this.enemyData.speedRolling;
        destroyed = 1;
        this.setAngle();
        this.beginRollAnimation();
      }
    } else if (this.rollData.rollCounter === this.rollData.rollLimit) {
      if (this.rollData.hpCurrent === 0) {
        if (!this.isBoss) destroyed = 2;
      } else {
        this.baseSpeed = this.enemyData.speedNormal;
        this.rollData.rollCounter = 0;
        this.rollData.rolling = false;
        this.animSpeed = this.enemyData.animSpeed;
        this.rollData.secondary = false;
        this.deathCounter = 0;
        this.hitDelay = 0;
        destroyed = this.resetRollAnimation(destroyed);
        this.xTarget = this.xPos;
        this.yTarget = this.yPos;
        this.dead = false;
        this.moving = false;
        this.actionCounter = 0;
        this.flipped = false;
      }
    }
    return destroyed;
  }

  /** Idle-state steering: orbit the player, open fire, wind up a slash. */
  private chase(
    knightX: number,
    knightY: number,
    returnAction: number,
  ): number {
    let deadzone = false;
    const dx = this.xPos - knightX;
    const dy = this.yPos - knightY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.targetAngle =
      Math.atan2(
        knightY + this.enemyData.yOffset - this.yPos,
        knightX + this.enemyData.xOffset - this.xPos,
      ) *
      (180 / Math.PI);
    if (this.updateSpecialChase(knightX, dist)) return returnAction;
    if (dist < 350) {
      if (dist < 200) {
        if (dist < 125) {
          this.targetAngle += 180;
        } else {
          deadzone = true;
        }
      }
      this.shotData.inRange = 1;
      if (this.slashData.slasher) {
        ++this.slashData.counter;
        returnAction = this.updateMeleeCharge(returnAction);
        if (this.slashData.counter >= this.slashData.freq) {
          if (this.flipped) {
            this.frame = this.slashData.freezeFrame + 1;
          } else {
            this.frame = this.slashData.freezeFrame;
          }
          this.slashData.counter = 0;
          this.frameCounter = 0;
          this.animSpeed = 99999;
          this.drawTile(this.frame);
          this.rollData.slashState = 1;
          this.targetAngle =
            Math.atan2(knightY - this.yPos, knightX - this.xPos) *
            (180 / Math.PI);
        }
      }
    } else {
      this.shotData.inRange = 0;
    }
    let speed = this.baseSpeed + dist * 0.015;
    if (speed > this.baseSpeed * 3) {
      speed = this.baseSpeed * 3;
    }
    if (deadzone === false) {
      const rad = this.targetAngle * (Math.PI / 180);
      this.xSpeed = speed * Math.cos(rad);
      this.ySpeed = speed * Math.sin(rad);
    } else {
      this.slowDown(0.12);
    }
    --this.enemyData.angleCounter;
    if (this.enemyData.angleCounter === 0) {
      this.enemyData.angleCounter = this.enemyData.angleLimit;
      this.setNewAngle();
    }
    return returnAction;
  }

  /** Slash and mine states. They cascade within one tick, so these stay sequential ifs. */
  private updateSlash(
    knightX: number,
    knightY: number,
    returnAction: number,
  ): number {
    ++this.slashData.counter;
    ++this.rollData.counter;
    if (this.rollData.slashState !== 13 && this.rollData.counter >= 5) {
      this.rollData.counter = 0;
    }
    if (this.rollData.slashState === 1) {
      this.slowDown(0.07);
      if (this.slashData.counter >= 30) {
        const slashRad = this.targetAngle * (Math.PI / 180);
        this.xSpeed = 9 * Math.cos(slashRad);
        this.ySpeed = 9 * Math.sin(slashRad);
        this.slashData.counter = 0;
        this.rollData.slashState = 2;
      }
    }
    if (this.rollData.slashState === 2) {
      const dxSl = this.xPos - knightX;
      const dySl = this.yPos - knightY;
      if (Math.sqrt(dxSl * dxSl + dySl * dySl) < this.slashData.slashRange) {
        this.rollData.slashState = 3;
        this.animSpeed = this.enemyData.animSpeed;
        this.frameLimit = this.slashData.frameLimit;
        this.frame = 0;
        this.slashData.counter = 0;
        this.animOffset = this.flipped
          ? this.slashData.animOffset + this.slashData.frameLimit
          : this.slashData.animOffset;
        returnAction = this.slashAction(this.flipped);
      }
      if (this.slashData.counter >= 50) {
        this.endSlash();
      }
    }
    if (this.rollData.slashState === 3 || this.rollData.slashState === 4) {
      if (this.slashData.counter === 1) {
        this.rollData.slashState = 4;
      }
      if (this.slashData.counter >= 55) {
        this.endSlash();
      }
      this.slowDown(0.07);
    }
    return this.updateSpecialSlash(knightX, knightY, returnAction);
  }

  private endSlash(): void {
    this.rollData.slashState = 0;
    this.animOffset = 0;
    this.frameLimit = this.enemyData.frameLimitNormal;
    this.frame = 0;
    this.frameCounter = 0;
    this.flipped = false;
    this.slashData.counter = 0;
    this.animSpeed = this.enemyData.animSpeed;
  }

  protected setAngle(): void {
    const rad = this.targetAngle * (Math.PI / 180);
    this.xSpeed = this.baseSpeed * Math.cos(rad);
    this.ySpeed = this.baseSpeed * Math.sin(rad);
  }

  private setNewAngle(): void {
    const rad = Math.ceil(Math.random() * 360) * (Math.PI / 180);
    this.enemyData.xOffset = this.enemyData.playerDist * Math.cos(rad);
    this.enemyData.yOffset = this.enemyData.playerDist * Math.sin(rad);
  }

  protected setActionLimit(mode: number = 0): void {
    if (mode === 0) {
      this.actionLimit = 46;
    }
    if (this.enemyData.stopper === false) {
      return;
    }
    if (mode === 1) {
      this.actionLimit = this.moving
        ? Math.ceil(Math.random() * 180) + 120
        : Math.ceil(Math.random() * 90) + 50;
    }
    this.actionCounter = 0;
  }

  protected slowDown(mult: number): void {
    this.xSpeed += (0 - this.xSpeed) * mult;
    this.ySpeed += (0 - this.ySpeed) * mult;
  }

  private drawTile(frame: number): void {
    this.tileRect.x = ((frame % this.rowLength) | 0) * this.tileSize;
    this.tileRect.y = ((frame / this.rowLength) | 0) * this.tileSize;
  }

  protected updateBehaviorAnimation(destroyed: number): number {
    return destroyed;
  }

  protected onCollision(_moveAllowed: readonly boolean[]): void {}
  protected beginRollAnimation(): void {
    this.frameLimit = this.enemyData.frameLimitRoll;
    this.frame = 0;
    this.frameCounter = 0;
    this.animSpeed = 2;
    this.animOffset =
      !this.isBoss && this.xSpeed <= 0
        ? this.enemyData.animOffsetRoll + this.enemyData.frameLimitRoll
        : this.enemyData.animOffsetRoll;
  }

  protected resetRollAnimation(destroyed: number): number {
    this.animOffset = 0;
    this.frameCounter = 0;
    this.frame = 0;
    this.frameLimit = this.enemyData.frameLimitNormal;
    return destroyed;
  }

  protected updateRollingBehavior(returnAction: number): number {
    return returnAction;
  }

  protected updateSpecialChase(_knightX: number, _dist: number): boolean {
    return false;
  }

  protected updateMeleeCharge(returnAction: number): number {
    return returnAction;
  }

  protected slashAction(_flipped: boolean): number {
    return 0;
  }

  protected updateSpecialSlash(
    _knightX: number,
    _knightY: number,
    returnAction: number,
  ): number {
    return returnAction;
  }
}

function rollAuthored(value: AuthoredNumber): number {
  if (typeof value === "number") return value;
  const step = value.step ?? 1;
  const spread = (value.maxInclusive - value.minExclusive) / step;
  return Math.ceil(Math.random() * spread) * step + value.minExclusive;
}
